import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Eye, EyeOff, Mail, Lock, Building2,
  Globe, Check, Sparkles, ArrowLeft, Loader2
} from 'lucide-react';
import { onboardingAPI, publicAPI } from '../../services/api';
import LandingNavbar from '../../components/LandingNavbar';

const Register = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    nameAr: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    planSlug: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const res = await publicAPI.getPlans();
      setPlans(res.data?.data || res.data || []);
    } catch {
      toast.error('تعذر تحميل الباقات');
    } finally {
      setPlansLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.nameAr.trim()) errs.nameAr = 'اسم المختبر بالعربي مطلوب';
    if (!form.name.trim()) errs.name = 'اسم المختبر بالإنجليزي مطلوب';
    if (!form.email.trim()) {
      errs.email = 'البريد الإلكتروني مطلوب';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = 'البريد الإلكتروني غير صحيح';
    }
    if (!form.password) {
      errs.password = 'كلمة المرور مطلوبة';
    } else if (form.password.length < 8) {
      errs.password = 'كلمة المرور يجب أن تكون 8 أحرف على الأقل';
    } else if (!/[A-Z]/.test(form.password) || !/[a-z]/.test(form.password) || !/[0-9]/.test(form.password)) {
      errs.password = 'يجب أن تحتوي على حرف كبير وصغير ورقم';
    }
    if (!form.confirmPassword) {
      errs.confirmPassword = 'تأكيد كلمة المرور مطلوب';
    } else if (form.password !== form.confirmPassword) {
      errs.confirmPassword = 'كلمة المرور غير متطابقة';
    }
    if (!form.planSlug) errs.planSlug = 'يرجى اختيار الباقة';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await onboardingAPI.register({
        name: form.name,
        nameAr: form.nameAr,
        email: form.email,
        password: form.password,
        planSlug: form.planSlug,
      });

      const data = res.data?.data || res.data;
      if (data?.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user || data));
        if (data.tenant) {
          localStorage.setItem('onboarding_tenant', JSON.stringify(data.tenant));
        }
      }

      toast.success('تم التسجيل بنجاح! مرحبا بك');
      navigate('/onboarding/setup');
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'حدث خطأ أثناء التسجيل';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = plans.find(p => p.slug === form.planSlug);
  const hasTrial = selectedPlan?.trialDays > 0;

  const benefits = [
    'إدارة الفحوصات والنتائج',
    'تقارير مالية وإحصائية',
    'ربط مع الأجهزة الطبية',
    'بوابة العملاء الإلكترونية',
  ];

  return (
    <>
      <LandingNavbar />
      {/* Single-column focused onboarding. The split-screen left panel with
          a decorative green gradient + "bullet benefits" used to dominate
          attention and push the form to a cramped right pane. Registration
          is the single most important step — the form deserves centre stage. */}
      <div
        className="min-h-screen pt-[120px] pb-16 px-4 relative"
        dir="rtl"
        style={{ fontFamily: "'Readex Pro', sans-serif", lineHeight: 1.7, backgroundColor: '#FDFCF9' }}
      >
        {/* Faint dotted texture so the wide canvas doesn't feel empty. */}
        <div
          className="absolute inset-0 opacity-[0.4] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #0F6E56 0.8px, transparent 0)',
            backgroundSize: '28px 28px',
            maskImage: 'radial-gradient(ellipse at top, rgba(0,0,0,0.18), transparent 60%)',
            WebkitMaskImage: 'radial-gradient(ellipse at top, rgba(0,0,0,0.18), transparent 60%)',
          }}
        />

      <div className="relative z-10 max-w-[680px] mx-auto">
        {/* Header outside the card so the trust-building copy (benefits,
            social proof) gets breathing room and SEO-friendly headings. */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-8"
        >
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium mb-4"
            style={{ backgroundColor: '#0F6E560F', color: '#0F6E56' }}
          >
            <Sparkles className="w-3 h-3" />
            ابدأ الآن — تجربة مجانية
          </span>
          <h1 className="text-[2rem] md:text-[2.25rem] font-bold text-[#1A1A18] mb-2 leading-tight tracking-tight">
            سجّل مختبرك خلال دقيقتين
          </h1>
          <p className="text-[#6A6B65] text-[15px] max-w-[480px] mx-auto leading-relaxed">
            نظام متكامل لإدارة الحجوزات، الفوترة، النتائج، والفريق — بدون تنصيب، يعمل من المتصفح مباشرة.
          </p>
          {/* Compact benefits strip — replaces the old left-panel list. */}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[12px] text-[#888780]">
            {benefits.slice(0, 4).map((b, i) => (
              <span key={i} className="inline-flex items-center gap-1.5">
                <Check className="w-3 h-3 text-[#0F6E56]" />
                {b}
              </span>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.08 }}
          className="w-full"
        >
          <div className="bg-white rounded-3xl border border-[#E8E5DD] shadow-[0_10px_40px_-10px_rgba(15,110,86,0.12)] p-6 md:p-10">
            <div className="mb-6 pb-6 border-b border-[#F0EDE5]">
              <h2 className="text-xl font-bold text-[#1A1A18] leading-[1.4]">معلومات المختبر</h2>
              <p className="text-[#888780] text-sm mt-1">نستخدمها لإنشاء حسابك. يمكنك تعديلها لاحقاً.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Lab Name Arabic */}
              <div>
                <label className="block text-sm font-medium text-[#2C2C2A] mb-1 leading-[1.7]">
                  اسم المختبر (عربي) <span className="text-[#E24B4A]">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-[#888780]" />
                  <input
                    type="text"
                    value={form.nameAr}
                    onChange={e => handleChange('nameAr', e.target.value)}
                    placeholder="مثال: مختبرات النور الطبية"
                    className={`w-full pr-10 pl-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all bg-[#FDFCF9] ${
                      errors.nameAr ? 'border-[#E24B4A]/40 focus:ring-[#E24B4A]/20' : 'border-gray-200 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75]'
                    }`}
                  />
                </div>
                {errors.nameAr && <p className="text-[#E24B4A] text-xs mt-1">{errors.nameAr}</p>}
              </div>

              {/* Lab Name English */}
              <div>
                <label className="block text-sm font-medium text-[#2C2C2A] mb-1 leading-[1.7]">
                  Lab Name (English) <span className="text-[#E24B4A]">*</span>
                </label>
                <div className="relative">
                  <Globe className="absolute right-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-[#888780]" />
                  <input
                    type="text"
                    dir="ltr"
                    value={form.name}
                    onChange={e => handleChange('name', e.target.value)}
                    placeholder="e.g. Al Noor Medical Labs"
                    className={`w-full pr-10 pl-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all text-left bg-[#FDFCF9] ${
                      errors.name ? 'border-[#E24B4A]/40 focus:ring-[#E24B4A]/20' : 'border-gray-200 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75]'
                    }`}
                  />
                </div>
                {errors.name && <p className="text-[#E24B4A] text-xs mt-1">{errors.name}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-[#2C2C2A] mb-1 leading-[1.7]">
                  البريد الإلكتروني <span className="text-[#E24B4A]">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-[#888780]" />
                  <input
                    type="email"
                    dir="ltr"
                    value={form.email}
                    onChange={e => handleChange('email', e.target.value)}
                    placeholder="admin@lab.com"
                    className={`w-full pr-10 pl-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all text-left bg-[#FDFCF9] ${
                      errors.email ? 'border-[#E24B4A]/40 focus:ring-[#E24B4A]/20' : 'border-gray-200 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75]'
                    }`}
                  />
                </div>
                {errors.email && <p className="text-[#E24B4A] text-xs mt-1">{errors.email}</p>}
              </div>

              {/* Passwords Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2A] mb-1 leading-[1.7]">
                    كلمة المرور <span className="text-[#E24B4A]">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-[#888780]" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      dir="ltr"
                      value={form.password}
                      onChange={e => handleChange('password', e.target.value)}
                      placeholder="6 أحرف على الأقل"
                      className={`w-full pr-10 pl-10 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all text-left bg-[#FDFCF9] ${
                        errors.password ? 'border-[#E24B4A]/40 focus:ring-[#E24B4A]/20' : 'border-gray-200 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75]'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888780] hover:text-[#2C2C2A]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-[#E24B4A] text-xs mt-1">{errors.password}</p>}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2A] mb-1 leading-[1.7]">
                    تأكيد كلمة المرور <span className="text-[#E24B4A]">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-[#888780]" />
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      dir="ltr"
                      value={form.confirmPassword}
                      onChange={e => handleChange('confirmPassword', e.target.value)}
                      placeholder="أعد كتابة كلمة المرور"
                      className={`w-full pr-10 pl-10 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all text-left bg-[#FDFCF9] ${
                        errors.confirmPassword ? 'border-[#E24B4A]/40 focus:ring-[#E24B4A]/20' : 'border-gray-200 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75]'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888780] hover:text-[#2C2C2A]"
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="text-[#E24B4A] text-xs mt-1">{errors.confirmPassword}</p>}
                </div>
              </div>

              {/* Plan Selection */}
              <div>
                <label className="block text-sm font-medium text-[#2C2C2A] mb-2 leading-[1.7]">
                  اختيار الباقة <span className="text-[#E24B4A]">*</span>
                </label>
                {plansLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-[#1D9E75] animate-spin" />
                    <span className="text-[#888780] text-sm mr-2">جاري تحميل الباقات...</span>
                  </div>
                ) : plans.length === 0 ? (
                  <div className="text-center py-6 text-[#888780] text-sm">
                    لا توجد باقات متاحة حاليا
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {plans.map(plan => (
                      <motion.div
                        key={plan.slug || plan.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleChange('planSlug', plan.slug)}
                        className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all ${
                          form.planSlug === plan.slug
                            ? 'border-[#1D9E75] bg-[#E1F5EE]/40 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        {form.planSlug === plan.slug && (
                          <div className="absolute top-2 left-2 w-5 h-5 bg-[#1D9E75] rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                        {plan.isPopular && (
                          <div className="absolute -top-2 right-3 bg-[#1D9E75] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                            الأكثر طلبا
                          </div>
                        )}
                        <h4 className="font-bold text-[#2C2C2A] text-sm mb-1 leading-[1.7]">{plan.nameAr || plan.name}</h4>
                        <div className="flex items-baseline gap-1 mb-2">
                          <span className="text-xl font-bold text-[#0F6E56]">{plan.monthlyPrice || 0}</span>
                          <span className="text-xs text-[#888780]">ر.س / شهريا</span>
                        </div>
                        {plan.trialDays > 0 && (
                          <div className="inline-flex items-center gap-1 bg-[#E1F5EE] text-[#0F6E56] text-[11px] font-medium px-2 py-0.5 rounded-full mb-2">
                            <Sparkles className="w-3 h-3" />
                            {plan.trialDays} يوم تجربة مجانية
                          </div>
                        )}
                        {/* Admin-written bullet highlights — never the raw
                            technical features[] array (that's internal). */}
                        <ul className="space-y-1">
                          {(plan.highlightPoints || []).slice(0, 4).map((h, i) => {
                            const text = typeof h === 'string' ? h : (h?.text || h?.textEn || '');
                            if (!text) return null;
                            return (
                              <li key={i} className="text-xs text-[#888780] flex items-center gap-1.5 leading-[1.8]">
                                <Check className="w-3 h-3 text-[#1D9E75] flex-shrink-0" />
                                {text}
                              </li>
                            );
                          })}
                        </ul>
                      </motion.div>
                    ))}
                  </div>
                )}
                {errors.planSlug && <p className="text-[#E24B4A] text-xs mt-1">{errors.planSlug}</p>}
              </div>

              {/* Submit */}
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: loading ? 1 : 1.01 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
                className="w-full py-3 text-white rounded-xl font-medium text-sm transition-all shadow-lg
                  disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ backgroundColor: '#1D9E75', boxShadow: '0 8px 24px rgba(29, 158, 117, 0.25)' }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    جاري التسجيل...
                  </>
                ) : (
                  <>
                    <ArrowLeft className="w-4 h-4" />
                    {hasTrial ? 'ابدأ تجربتك المجانية' : 'سجل الآن'}
                  </>
                )}
              </motion.button>
            </form>

            {/* Login Link */}
            <div className="text-center mt-5 pt-5 border-t border-gray-100">
              <p className="text-sm text-[#888780] leading-[1.8]">
                عندك حساب؟{' '}
                <Link to="/staff-login" className="text-[#0F6E56] font-medium hover:text-[#1D9E75] transition-colors">
                  سجل دخول
                </Link>
              </p>
            </div>
          </div>

          <p className="text-center text-xs text-[#888780]/60 mt-4 leading-[1.8]">
            بتسجيلك فإنك توافق على شروط الاستخدام وسياسة الخصوصية
          </p>
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default Register;
